import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Display = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🖼️ CSS Display</h1>

      <p className="tutorial-intro">
        The <code>display</code> property in CSS defines how an element is shown
        on the page. It is one of the most important properties because it
        determines the layout behavior of elements. Common values are{" "}
        <code>block</code>, <code>inline</code>, <code>inline-block</code>, and{" "}
        <code>none</code>.
      </p>

      {/* Highlight Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#0369a1", marginBottom: "0.5rem" }}>
          ✨ Why Learn display?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          The <code>display</code> property controls how elements stack, align,
          and take up space. Mastering it is essential for building page
          layouts.
        </p>
      </div>

      {/* Block */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 display: block</h2>
        <p>
          A block-level element always starts on a new line and takes up the
          full width available. Examples: <code>&lt;div&gt;</code>,{" "}
          <code>&lt;p&gt;</code>, <code>&lt;h1&gt;</code>.
        </p>

        <CodeCopyBlock
          code={`div {
  display: block;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <div
            style={{
              display: "block",
              background: "#bbf7d0",
              padding: "10px",
              marginBottom: "10px",
            }}
          >
            Block Element 1
          </div>
          <div
            style={{
              display: "block",
              background: "#86efac",
              padding: "10px",
            }}
          >
            Block Element 2
          </div>
        </div>
      </div>

      {/* Inline */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 display: inline</h2>
        <p>
          Inline elements do not start on a new line and only take up as much
          width as needed. Examples: <code>&lt;span&gt;</code>,{" "}
          <code>&lt;a&gt;</code>, <code>&lt;strong&gt;</code>.
        </p>

        <CodeCopyBlock
          code={`span {
  display: inline;
}`}
        />

        <div
          style={{ marginTop: "1rem", background: "#f0f9ff", padding: "10px" }}
        >
          <span
            style={{
              display: "inline",
              background: "#93c5fd",
              padding: "5px",
              margin: "2px",
            }}
          >
            Inline 1
          </span>
          <span
            style={{
              display: "inline",
              background: "#60a5fa",
              padding: "5px",
              margin: "2px",
            }}
          >
            Inline 2
          </span>
          <span
            style={{
              display: "inline",
              background: "#3b82f6",
              padding: "5px",
              margin: "2px",
              color: "white",
            }}
          >
            Inline 3
          </span>
        </div>
      </div>

      {/* Inline-block */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 display: inline-block</h2>
        <p>
          An inline-block element is similar to inline, but you can set width
          and height. They sit next to each other while keeping block-like
          properties.
        </p>

        <CodeCopyBlock
          code={`div {
  display: inline-block;
  width: 120px;
  height: 50px;
}`}
        />

        <div
          style={{ marginTop: "1rem", background: "#fef9c3", padding: "10px" }}
        >
          <div
            style={{
              display: "inline-block",
              width: "120px",
              height: "50px",
              background: "#facc15",
              margin: "5px",
              textAlign: "center",
              lineHeight: "50px",
            }}
          >
            Box 1
          </div>
          <div
            style={{
              display: "inline-block",
              width: "120px",
              height: "50px",
              background: "#eab308",
              margin: "5px",
              textAlign: "center",
              lineHeight: "50px",
            }}
          >
            Box 2
          </div>
        </div>
      </div>

      {/* None */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 display: none</h2>
        <p>
          The <code>display: none;</code> property hides an element completely
          (it will not take up any space in the layout).
        </p>

        <CodeCopyBlock
          code={`p {
  display: none;
}`}
        />

        <div
          style={{
            marginTop: "1rem",
            background: "#fee2e2",
            padding: "10px",
            borderRadius: "6px",
          }}
        >
          <p style={{ display: "none" }}>This text is hidden (none)</p>
          <p>This text is visible</p>
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          You’ve learned the basics of <code>display</code>. Next, explore{" "}
          <a href="/css/position" className="learn-more">
            CSS Position
          </a>{" "}
          to control where elements appear on the page.
        </p>
      </div>
    </div>
  );
};

export default CSS_Display;
